import { assertUnreachable, isNonEmpty } from '@sgme/fp';
import { getTime } from 'date-fns';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { QuoteDetailsData } from 'state/quotes/details/details.model';
import type { QuoteInfos } from 'state/quotes/infos/infos.model';
import type { PendingQuoteDetailsData } from 'state/quotes/pendingDetails/pendingDetails.model';
import type { QuoteResult } from 'state/quotes/results/result.model';
import type { TradeInfos } from 'state/quotes/trades/trades.model';

import type {
  QuoteData,
  QuotedTileData,
  QuotingTileData,
  RejectedWithoutPriceTileData,
  RejectedWithPriceTileData,
  TechnicalErrorWithoutPriceTileData,
  TechnicalErrorWithPriceTileData,
  TileData,
  TimeInterval,
  TradeableTileData,
  TradedTileData,
  TradingTileData,
} from '../model';

function _isTradeable(infos: QuoteInfos) {
  return (
    infos.quoteType === 'Tradable' ||
    infos.quoteType === 'AdvancedTradable' ||
    infos.quoteType === 'ExecOnly' ||
    infos.quoteType === 'AdvancedExecOnly'
  );
}

function _getSolveMode(details: QuoteDetailsData) {
  return details.SolvingMode;
}

function _getPrice(details: QuoteDetailsData, result: QuoteResult): QuoteData {
  return {
    solveMode: _getSolveMode(details),
    solveResult: result?.solveResult ?? 0.0,
    solveResultFormat: result?.solveResultFormat,
    xMargin: details.XMargin!,
  } as QuoteData;
}

function _getUnderlyings(details: QuoteDetailsData) {
  return details.Underlyings;
}

const _getProductSubProductAndWrapper = (details: QuoteDetailsData) => ({
  product: details.ProductType ?? details.InstrumentName!,
  subProduct: details.ProductSubtype ?? details.InstrumentContractName!,
  wrapper: details.Wrapper,
});

function _getLastUpdated(infos: QuoteInfos, reloadedTile: TileData | undefined): Date {
  return reloadedTile?.lastUpdated ?? infos.lastUpdated;
}

function getQuotedTileData(
  infos: QuoteInfos,
  details: QuoteDetailsData,
  result: QuoteResult,
  reloadedTile: TileData | undefined,
  formData: any | undefined,
) {
  return {
    status: 'Quoted',
    clientId: details.SelectedOnBehalfClient?.id,
    clientName: details.SelectedOnBehalfClient?.label,
    underlyings: _getUnderlyings(details),
    isTradeable: _isTradeable(infos),
    externalQuoteId: result.quoteExternalId,
    ..._getProductSubProductAndWrapper(details),
    ..._getPrice(details, result),
    maturityDate: details.MaturityDate,
    tenor: details.Tenor,
    currency: details.Currency,
    workspace: infos.workspace,
    lastUpdated: _getLastUpdated(infos, reloadedTile),
    remunerationMode: formData?.remunerationMode,
    fixedAmountInPercent: formData?.fixedAmountInPercent,
  } as QuotedTileData;
}

function getRejectedWithoutPriceTileData(
  infos: QuoteInfos,
  details: QuoteDetailsData,
  externalQuoteId: string,
  rejections: NextWorkflowError[],
  isInternalUser: boolean,
  reloadedTile: TileData | undefined,
  formData: any | undefined,
) {
  return {
    status: 'RejectedWithoutPrice',
    clientId: details.SelectedOnBehalfClient?.id,
    clientName: details.SelectedOnBehalfClient?.label,
    underlyings: _getUnderlyings(details),
    solveMode: _getSolveMode(details),
    isTradeable: _isTradeable(infos),
    externalQuoteId,
    ..._getProductSubProductAndWrapper(details),
    maturityDate: details.MaturityDate,
    tenor: details.Tenor,
    currency: details.Currency,

    errorMessages: rejections,
    shouldUseRejectionCode: !isInternalUser,
    shouldDisplayErrorDetails: isInternalUser,
    workspace: infos.workspace,
    lastUpdated: _getLastUpdated(infos, reloadedTile),
    remunerationMode: formData?.remunerationMode,
    fixedAmountInPercent: formData?.fixedAmountInPercent,
  } as RejectedWithoutPriceTileData;
}

function getRejectedWithPriceTileData(
  infos: QuoteInfos,
  details: QuoteDetailsData,
  result: QuoteResult,
  rejections: NextWorkflowError[],
  isInternalUser: boolean,
  reloadedTile: TileData | undefined,
  formData: any | undefined,
) {
  return {
    ...getRejectedWithoutPriceTileData(
      infos,
      details,
      result.quoteExternalId,
      rejections,
      isInternalUser,
      reloadedTile,
      formData,
    ),
    status: 'RejectedWithPrice',
    ..._getPrice(details, result),
  } as RejectedWithPriceTileData;
}

function _getQuoteTechnicalErrorTileData(
  infos: QuoteInfos,
  details: QuoteDetailsData,
  externalQuoteId: string,
  errorMessage: ApiErrorMessage,
  reloadedTile: TileData | undefined,
  formData: any | undefined,
) {
  return {
    status: 'TechnicalErrorWithoutPrice',
    clientId: details.SelectedOnBehalfClient?.id,
    clientName: details.SelectedOnBehalfClient?.label,
    underlyings: _getUnderlyings(details),
    solveMode: _getSolveMode(details),
    isTradeable: _isTradeable(infos),
    externalQuoteId,
    ..._getProductSubProductAndWrapper(details),
    maturityDate: details.MaturityDate,
    tenor: details.Tenor,
    currency: details.Currency,
    errorMessage,
    workspace: infos.workspace,
    lastUpdated: _getLastUpdated(infos, reloadedTile),
    remunerationMode: formData?.remunerationMode,
    fixedAmountInPercent: formData?.fixedAmountInPercent,
  } as TechnicalErrorWithoutPriceTileData;
}

function _getTradeAuthTechnicalErrorTileData(
  infos: QuoteInfos,
  details: QuoteDetailsData,
  result: QuoteResult,
  errorMessage: ApiErrorMessage,
  reloadedTile: TileData | undefined,
  formData: any | undefined,
) {
  return {
    status: 'TechnicalErrorWithPrice',
    clientId: details.SelectedOnBehalfClient?.id,
    clientName: details.SelectedOnBehalfClient?.label,
    underlyings: _getUnderlyings(details),
    isTradeable: _isTradeable(infos),
    externalQuoteId: result.quoteExternalId,
    ..._getProductSubProductAndWrapper(details),
    maturityDate: details.MaturityDate,
    tenor: details.Tenor,
    currency: details.Currency,
    errorMessage,
    ..._getPrice(details, result),
    workspace: infos.workspace,
    lastUpdated: _getLastUpdated(infos, reloadedTile),
    remunerationMode: formData?.remunerationMode,
    fixedAmountInPercent: formData?.fixedAmountInPercent,
  } as TechnicalErrorWithPriceTileData;
}

function getQuotingTileData(
  infos: QuoteInfos,
  details: QuoteDetailsData,
  externalQuoteId: string | undefined,
  pendingDetails: PendingQuoteDetailsData,
  reloadedTile: TileData | undefined,
  formData: any | undefined,
) {
  return {
    status: 'Quoting',
    clientName: details.SelectedOnBehalfClient?.label,
    clientId: details.SelectedOnBehalfClient?.id,
    underlyings: _getUnderlyings(details),
    solveMode: _getSolveMode(details),
    isTradeable: _isTradeable(infos),
    externalQuoteId,
    ..._getProductSubProductAndWrapper(details),
    pricing: {
      start: getTime(pendingDetails.start),
      durationInMs: pendingDetails.durationInMs,
    },
    workspace: infos.workspace,
    lastUpdated: _getLastUpdated(infos, reloadedTile),
    remunerationMode: formData?.remunerationMode,
    fixedAmountInPercent: formData?.fixedAmountInPercent,
  } as QuotingTileData;
}

function getTradeableTileData(
  infos: QuoteInfos,
  details: QuoteDetailsData,
  result: QuoteResult,
  tradeInfos: TradeInfos,
  isQuoteLive: boolean,
  reloadedTile: TileData | undefined,
  formData: any | undefined,
) {
  const tradeAuth: TimeInterval = {
    start: tradeInfos.tradeAuthStartTimeInMillis!,
    durationInMs: tradeInfos.tradeAuthDurationInMillis!,
  };
  const isTradeable = _isTradeable(infos);
  const canTrade = tradeAuth !== undefined && isQuoteLive;
  return {
    status: 'Tradeable',
    clientId: details.SelectedOnBehalfClient?.id,
    clientName: details.SelectedOnBehalfClient?.label,
    underlyings: _getUnderlyings(details),
    externalQuoteId: result.quoteExternalId,
    isTradeable,
    ..._getProductSubProductAndWrapper(details),
    maturityDate: details.MaturityDate,
    tenor: details.Tenor,
    currency: details.Currency,
    tradeAuth,
    ..._getPrice(details, result),
    canTrade,
    workspace: infos.workspace,
    lastUpdated: _getLastUpdated(infos, reloadedTile),
    remunerationMode: formData?.remunerationMode,
    fixedAmountInPercent: formData?.fixedAmountInPercent,
  } as TradeableTileData;
}

function getTradingTileData(
  infos: QuoteInfos,
  details: QuoteDetailsData,
  result: QuoteResult,
  reloadedTile: TileData | undefined,
  formData: any | undefined,
) {
  return {
    status: 'Trading',
    clientId: details.SelectedOnBehalfClient?.id,
    clientName: details.SelectedOnBehalfClient?.label,
    underlyings: _getUnderlyings(details),
    externalQuoteId: result.quoteExternalId,
    isTradeable: _isTradeable(infos),
    ..._getProductSubProductAndWrapper(details),
    maturityDate: details.MaturityDate,
    tenor: details.Tenor,
    currency: details.Currency,
    ..._getPrice(details, result),
    workspace: infos.workspace,
    lastUpdated: _getLastUpdated(infos, reloadedTile),
    remunerationMode: formData?.remunerationMode,
    fixedAmountInPercent: formData?.fixedAmountInPercent,
  } as TradingTileData;
}

function getTradedTileData(
  infos: QuoteInfos,
  details: QuoteDetailsData,
  result: QuoteResult,
  reloadedTile: TileData | undefined,
  formData: any | undefined,
) {
  return {
    status: 'Traded',
    clientId: details.SelectedOnBehalfClient?.id,
    clientName: details.SelectedOnBehalfClient?.label,
    underlyings: _getUnderlyings(details),
    externalQuoteId: result.quoteExternalId,
    isTradeable: _isTradeable(infos),
    ..._getProductSubProductAndWrapper(details),
    maturityDate: details.MaturityDate,
    tenor: details.Tenor,
    currency: details.Currency,
    ..._getPrice(details, result),
    workspace: infos.workspace,
    lastUpdated: _getLastUpdated(infos, reloadedTile),
    remunerationMode: formData?.remunerationMode,
    fixedAmountInPercent: formData?.fixedAmountInPercent,
  } as TradedTileData;
}

export function _getTileData(
  isInternalUser: boolean,
  infos: QuoteInfos,
  details: QuoteDetailsData,
  result: QuoteResult,
  pendingDetails: PendingQuoteDetailsData,
  tradeInfos: TradeInfos,
  isQuoteLive: boolean,
  reloadedTile: TileData | undefined,
  formData: any,
): TileData | undefined {
  if (infos === undefined || details === undefined) {
    return reloadedTile;
  }

  if (result !== undefined) {
    switch (result.status) {
      case 'Quoted':
        switch (tradeInfos?.tradeAuthStatus) {
          case undefined:
          case 'Pending':
            return getQuotedTileData(infos, details, result, reloadedTile, formData);
          case 'Granted':
            switch (tradeInfos?.tradeStatus) {
              case undefined:
                return getTradeableTileData(infos, details, result, tradeInfos, isQuoteLive, reloadedTile, formData);
              case 'Pending':
                return getTradingTileData(infos, details, result, reloadedTile, formData);
              case 'Timeout':
              case 'Failed':
                return _getQuoteTechnicalErrorTileData(
                  infos,
                  details,
                  result.quoteExternalId,
                  tradeInfos.technicalError!,
                  reloadedTile,
                  formData,
                );
              case 'Rejected':
                const tradeRejections = tradeInfos.tradeRejections! as NextWorkflowError[];
                return getRejectedWithPriceTileData(
                  infos,
                  details,
                  result,
                  tradeRejections,
                  isInternalUser,
                  reloadedTile,
                  formData,
                );
              case 'Done':
                return getTradedTileData(infos, details, result, reloadedTile, formData);
              default:
                return assertUnreachable(tradeInfos?.tradeStatus, 'Unhandled trade status');
            }
          case 'DerogationRequired':
          case 'Denied':
          case 'Failed':
            const tradeAuthRejections = tradeInfos.tradeAuthRejections! as NextWorkflowError[];
            return getRejectedWithPriceTileData(
              infos,
              details,
              result,
              tradeAuthRejections,
              isInternalUser,
              reloadedTile,
              formData,
            );
          case 'Timeout':
            return _getTradeAuthTechnicalErrorTileData(
              infos,
              details,
              result,
              {
                message: 'quote.errorMessages.tradeAuthorizationTimeout',
              } as ApiErrorMessage,
              reloadedTile,
              formData,
            );
          default:
            return assertUnreachable(tradeInfos?.tradeAuthStatus, 'Unhandled trade auth status');
        }
      case 'Rejected':
      case 'Failed':
        if (isNonEmpty(result.rejections)) {
          return getRejectedWithoutPriceTileData(
            infos,
            details,
            result.quoteExternalId,
            result.rejections,
            isInternalUser,
            reloadedTile,
            formData,
          );
        }
        break;
      case undefined:
        if (result.technicalError !== undefined) {
          return _getQuoteTechnicalErrorTileData(
            infos,
            details,
            result.quoteExternalId,
            result.technicalError,
            reloadedTile,
            formData,
          );
        }
    }
  }

  if (pendingDetails !== undefined) {
    return getQuotingTileData(infos, details, result?.quoteExternalId, pendingDetails, reloadedTile, formData);
  }

  return undefined;
}
